import FilePondPluginImageExifOrientation from "filepond-plugin-image-exif-orientation";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";
import "filepond/dist/filepond.min.css";
import React, { useEffect, useRef, useState } from "react";
import { registerPlugin } from "react-filepond";
import { Controller, useForm } from "react-hook-form";
import { Label, Modal, ModalBody, ModalHeader } from "reactstrap";

import { ApproveButton, CancelButton, CloseButton, ReceiveButton, RejectButton, ReturnButton, RevertButton } from "@/components/Common/Buttons";
import FileUpload from "@/components/Common/FileUpload";
import { DatePickerField, InputField } from "@/components/Common/Inputs";
import { useNotificationModal } from "@/context/notificationContext";

import {
  useApproveOrderRequestsMutation,
  useCancelOrderRequestsMutation,
  useReceiveOrderRequestsMutation,
  useRejectOrderRequestsMutation,
  useReturnOrderRequestsMutation,
  useRevertOrderRequestsMutation,
} from "@/api/Endpoints/Globo/Transactions/OrderRequest/OrderRequests";

registerPlugin(FilePondPluginImageExifOrientation, FilePondPluginImagePreview);

const MODULE_NAME = "Order Request";

const ActionModal = ({ orderRequestId, data = null, show, onCloseClick, customFunction }) => {
  const actionType = useRef("save");
  const [files, setFiles] = useState([]);
  const { notification } = useNotificationModal();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      orderRequestId: orderRequestId || 0,
      remarks: "",
      invoiceNo: "",
    },
  });

  useEffect(() => {
    if (orderRequestId !== 0) {
      reset({
        orderRequestId: orderRequestId || 0,
        remarks: "",
        invoiceNo: "",
      });
    } else {
      reset({
        orderRequestId: 0,
        remarks: "",
      });
    }
  }, [orderRequestId, show]);

  // Calling Action API
  const [returnAction, { isLoading: isReturning }] = useReturnOrderRequestsMutation();
  const [approveAction, { isLoading: isApproving }] = useApproveOrderRequestsMutation();
  const [receiveAction, { isLoading: isReceiving }] = useReceiveOrderRequestsMutation();
  const [rejectAction, { isLoading: isRejecting }] = useRejectOrderRequestsMutation();
  const [cancelAction, { isLoading: isCanceling }] = useCancelOrderRequestsMutation();
  const [revertAction, { isLoading: isReverting }] = useRevertOrderRequestsMutation();

  const renderMessage = (data) => {
    switch (data) {
      case "Approve":
        return "success";
      case "Receive":
        return "success";
      case "Return":
        return "primary";
      case "Reject":
        return "danger";
      case "Cancel":
        return "danger";
      case "Revert":
        return "warning";
      default:
        return "dark";
    }
  };

  const onSubmit = async (formData) => {
    const payload = {
      remarks: formData.remarks,
      dateReceived: formData.dateReceived,
      invoiceNo: formData.invoiceNo,
      orderRequestId: orderRequestId,
      files,
    };

    try {
      if (actionType.current === "Approve") {
        const approved = await approveAction(payload);
        if (approved?.data?.success) {
          customFunction.updateState({ data: approved?.data?.returnData });
          notification({
            type: "success",
            header: `Approved ${MODULE_NAME}`,
            title: approved?.data?.returnData.referenceNo,
            message: "Request was Approved",
          });
          onCloseClick(true);
        } else {
          onCloseClick();
          notification({ type: "error", title: MODULE_NAME, message: `${approved.data.returnMessage}` });
        }
      } else if (actionType.current === "Receive") {
        const received = await receiveAction(payload);
        if (received?.data?.success) {
          customFunction.updateState({ data: received?.data?.returnData });
          notification({
            type: "success",
            header: `Set as Received ${MODULE_NAME}`,
            title: received?.data?.returnData.referenceNo,
            message: "Order was Received",
          });
          onCloseClick(true);
        } else {
          onCloseClick();
          notification({ type: "error", title: MODULE_NAME, message: `${returned.data.returnMessage}` });
        }
      } else if (actionType.current === "Return") {
        const returned = await returnAction(payload);
        if (returned?.data?.success) {
          customFunction.updateState({ data: returned?.data?.returnData });
          notification({
            type: "reject",
            header: `Returned ${MODULE_NAME}`,
            title: returned?.data?.returnData.referenceNo,
            message: "Request was Returned",
          });
          onCloseClick(true);
        } else {
          onCloseClick();
          notification({ type: "error", title: MODULE_NAME, message: `${returned.data.returnMessage}` });
        }
      } else if (actionType.current === "Reject") {
        const rejected = await rejectAction(payload);
        if (rejected?.data?.success) {
          customFunction.updateState({ data: rejected?.data?.returnData });
          notification({
            type: "reject",
            header: `Rejected ${MODULE_NAME}`,
            title: rejected?.data?.returnData.referenceNo,
            message: "Request was Rejected",
          });
          onCloseClick(true);
        } else {
          onCloseClick();
          notification({ type: "error", title: MODULE_NAME, message: `${rejected.data.returnMessage}` });
        }
      } else if (actionType.current === "Cancel") {
        const canceled = await cancelAction(payload);
        if (canceled?.data?.success) {
          customFunction.updateState({ data: canceled?.data?.returnData });
          notification({
            type: "reject",
            header: `Canceled ${MODULE_NAME}`,
            title: canceled?.data?.returnData.referenceNo,
            message: "Request was Canceled",
          });
          onCloseClick(true);
        } else {
          onCloseClick();
          notification({ type: "error", title: MODULE_NAME, message: `${canceled.data.returnMessage}` });
        }
      } else if (actionType.current === "Revert") {
        const reverted = await revertAction(payload);
        if (reverted?.data?.success) {
          customFunction.updateState({ data: reverted?.data?.returnData });
          notification({
            type: "success",
            header: `Reverted ${MODULE_NAME}`,
            title: reverted?.data?.returnData.referenceNo,
            message: "Request was Reverted",
          });
          onCloseClick(true);
        } else {
          onCloseClick();
          notification({ type: "error", title: MODULE_NAME, message: `${reverted.data.returnMessage}` });
        }
      }
    } catch (error) {
      console.log({ error });
      notification({ type: "error", title: MODULE_NAME, message: "Something went wrong" });
    }
  };

  return (
    <React.Fragment>
      <Modal modalClassName="flip" id="requestSave" size="lg" isOpen={show} toggle={() => onCloseClick(false)} centered>
        <ModalHeader toggle={() => onCloseClick(false)} className={`p-3 bg-${renderMessage(data?.actionName)}-subtle text-center w-100`}>
          <center>{`${data?.actionName} ${data?.referenceNo} `}</center>
        </ModalHeader>
        <ModalBody>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-3 pb-2">
              {data?.actionName === "Receive" ? (
                <div>
                  <DatePickerField control={control} name="dateReceived" label="Date Received" rules={{ required: "Date Received is required" }} />
                  <InputField
                    name="invoiceNo"
                    control={control}
                    label="Invoice No."
                    type="text"
                    rules={{ required: "Invoice No. is required" }}
                    placeholder="Enter invoice no..."
                  />
                </div>
              ) : null}

              <Label htmlFor="exampleFormControlTextarea" className="form-label">
                Remarks
              </Label>
              <Controller
                name="remarks"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <InputField control={control} type="textarea" rows="4" maxLength={500} showCharCounter placeholder="Enter Remarks" {...field} />
                )}
              />
              <br />
              <FileUpload onFilesChange={setFiles} />
            </div>
            <div className="hstack gap-2 justify-content-end">
              <CloseButton onClick={onCloseClick} />
              {data?.actionName === "Return" ? (
                <ReturnButton isProcessing={isReturning} onClick={() => (actionType.current = data?.actionName)} />
              ) : data?.actionName === "Approve" ? (
                <ApproveButton isProcessing={isApproving} onClick={() => (actionType.current = data?.actionName)} />
              ) : data?.actionName === "Receive" ? (
                <ReceiveButton isProcessing={isReceiving} onClick={() => (actionType.current = data?.actionName)} />
              ) : data?.actionName === "Reject" ? (
                <RejectButton isProcessing={isRejecting} onClick={() => (actionType.current = data?.actionName)} />
              ) : data?.actionName === "Cancel" ? (
                <CancelButton isProcessing={isCanceling} onClick={() => (actionType.current = data?.actionName)} />
              ) : data?.actionName === "Revert" ? (
                <RevertButton isProcessing={isReverting} onClick={() => (actionType.current = data?.actionName)} />
              ) : (
                ""
              )}
            </div>
          </form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default ActionModal;
